import { PageContentLayout } from '@/creditflowresearch/ui/common/components/ContentLayouts';
import CorporateMarketProfile from '@/creditflowresearch/ui/components/profile/CorporateMarketProfile';
import { ProfileTypes } from '@/creditflowresearch/application/actions/profile/constants';
import withApplicationPropsV2 from '@/creditflowresearch/application/withApplicationPropsV2';
import getSSRCorporateTopCompanies
  from '@/creditflowresearch/application/TopCompanies/serverSide/getSSRCorporateTopCompanies';
import PageFiltersContextProvider from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import getServerSideProfileBreadcrumbs
  from '@/creditflowresearch/application/Breadcrumbs/serverSide/getServerSideProfileBreadcrumbs';
import ParentPageContextProvider from '@/creditflowresearch/application/ParentPageContext';
import getServerSideArticlesTile
  from '@/creditflowresearch/application/ArticlesAndStats/Articles/ArticlesTile/serverSide/getServerSideArticlesTile';
import getServerSideStatsTile
  from '@/creditflowresearch/application/ArticlesAndStats/Stats/StatsTile/serverSide/getServerSideStatsTile';
import { getProducts } from '@/creditflowresearch/application/Deal/helpers';
import getServerSideRoadshows
  from '@/creditflowresearch/application/Roadshows/serverSide/getServerSideRoadshows';
import getServerSideMarketPageCorporateDeals from '@/creditflowresearch/application/Deal/serverSide/getServerSideMarketPageCorporateDeals';
import getServerSideCorporateMarketChartData from '@/creditflowresearch/application/MarketChart/serverSide/getServerSideCorporateMarketChartData';
import { featureToggleServerSide } from '@framework/ui/Organisms/FeatureToggle/Components';
import { HIDE_TOP_COMPANIES } from '@creditflowresearch/config/featureToggle';
import { initialTopCompanies } from '@/creditflowresearch/application/TopCompanies/constants';
import { pageRedirectTo } from '@framework/router/Next/helpers';
import getDefaultQueryFilters from '@/creditflowresearch/application/helpers/getDefaultQueryFilters';
import { removeQueryParams } from '@framework/router';
import { getCorporateMarketUrlByType } from '@/creditflowresearch/application/helpers/getCorporateMarketUrlByType';
import { corporateValidators, getCorporatePreferenceDefaults } from '@/creditflowresearch/application/helpers/validators';

export const getServerSideProps = withApplicationPropsV2(async (context) => {
  const { query } = context;

  const queryParams = getDefaultQueryFilters({
    query: removeQueryParams(query, ['routeType', 'regions']),
    defaults: getCorporatePreferenceDefaults(
      context,
      getCorporateMarketUrlByType(query.routeType as string),
    ),
    validators: corporateValidators,
  });

  if (queryParams) {
    const url = getCorporateMarketUrlByType(query.routeType as string);
    return pageRedirectTo(`${ url }${ queryParams }`);
  }

  const { availableProductIds, activeProductId } = getProducts(context);

  const [
    chartData,
    deals,
    topParents,
    breadcrumbs,
    articlesTilesData,
    roadshowsTileData,
    statsTileData,
  ] = await Promise.all([
    getServerSideCorporateMarketChartData(context),
    getServerSideMarketPageCorporateDeals(context),
    featureToggleServerSide(context, {
      featureName: HIDE_TOP_COMPANIES,
      featureCallback: getSSRCorporateTopCompanies,
      featureCallbackParams: { checkProductAccess: true },
      defaultCallback: async () => initialTopCompanies,
    }),
    getServerSideProfileBreadcrumbs(context, ProfileTypes.OVERVIEW),
    getServerSideArticlesTile(context, { deal: null }),
    getServerSideRoadshows(context, { isTile: true }),
    getServerSideStatsTile(context),
  ]);

  return ({
    props: {
      breadcrumbs,
      topParents,
      deals,
      chartData,
      articlesTilesData,
      availableProductIds,
      activeProductId,
      roadshowsTileData,
      statsTileData,
    },
  });
}, {
  dictionaries: ['countries', 'sizeDescriptions', 'dealStatuses', 'currencies', 'rankings', 'ratings'],
  needsLegacyDictionaries: false,
});

const CorporateMarketOverview = (props) => (
  <PageFiltersContextProvider profileType={ ProfileTypes.OVERVIEW }>
    <ParentPageContextProvider parentProps={ {
      topParents: props?.topParents,
      deals: props?.deals,
      chartData: props?.chartData,
      articlesTilesData: props?.articlesTilesData,
      availableProductIds: props?.availableProductIds,
      activeProductId: props?.activeProductId,
      roadshowsTileData: props?.roadshowsTileData,
      statsTileData: props?.statsTileData,
    } }
    >
      <PageContentLayout>
        <CorporateMarketProfile />
      </PageContentLayout>
    </ParentPageContextProvider>
  </PageFiltersContextProvider>
);

export default CorporateMarketOverview;
